import React, { useState, useCallback } from "react";
import { Card, Form, Button, Alert } from "react-bootstrap";
import BrandSelect from "../common/BrandSelect";

function SuggestionForm({
  suggestion,
  setSuggestion,
  selectedSuggestionType,
  setSelectedSuggestionType,
  loadingSuggestionTypes,
  suggestionTypes,
  handleSuggestionSubmit,
  translations,
  textareaRef,
  loading,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBase64, setFileBase64] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");

  // Function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:type/subtype;base64, prefix to get only the base64 string
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle file selection
  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    setFileError("");

    if (file) {
      // Optional: Add file size validation (e.g., max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setFileError(translations.fileTooLarge);
        setSelectedFile(null);
        setFileBase64("");
        setFileName("");
        return;
      }

      try {
        setSelectedFile(file);
        const base64 = await convertFileToBase64(file);
        setFileBase64(base64);
        setFileName(file.name);
      } catch (error) {
        setFileError(translations.fileProcessingError);
        setSelectedFile(null);
        setFileBase64("");
        setFileName("");
      }
    } else {
      setSelectedFile(null);
      setFileBase64("");
      setFileName("");
    }
  }, [translations]);

  // Remove selected file
  const removeFile = useCallback(() => {
    setSelectedFile(null);
    setFileBase64("");
    setFileError("");
    setFileName("");
    // Clear the file input
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  }, []);

  // Handle form submission, passing suggestion, type and file base64
  const onSubmit = useCallback((e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("suggestion", suggestion);
    formData.append("suggestionType", selectedSuggestionType);

    // Add the base64 file to the imageBase64 field
    if (fileBase64) {
      formData.append("imageBase64", fileBase64);
      formData.append("nameImageBase64", fileName);
    }

    handleSuggestionSubmit(formData);
    removeFile();
  }, [suggestion, selectedSuggestionType, fileBase64, fileName, handleSuggestionSubmit, removeFile]);

  const handleSuggestionChange = useCallback((e) => {
  setSuggestion(e.target.value);
}, [setSuggestion]);

const handleSuggestionTypeChange = useCallback((e) => {
  setSelectedSuggestionType(e.target.value);
}, [setSelectedSuggestionType]);

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card className="mt-4">
      <Card.Header>{translations.sendSuggestion}</Card.Header>
      <Card.Body className="p-3">
        <Form onSubmit={onSubmit}>
          <Form.Group id="suggestion-textarea" className="mb-3">
            <Form.Control
              required
              as="textarea"
              ref={textareaRef}
              rows={2}
              placeholder={translations.typeSuggestion}
              value={suggestion}
              onChange={handleSuggestionChange}
              style={{ overflow: "auto", resize: "none", borderWidth: '4px' }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translations.suggestionTypeLabel}</Form.Label>
            <BrandSelect
              value={selectedSuggestionType}
              onChange={(v) => handleSuggestionTypeChange({ target: { value: v } })}
              disabled={loadingSuggestionTypes}
              placeholder={translations.selectSuggestionType}
              options={[
                { value: "", label: translations.selectSuggestionType },
                ...suggestionTypes.map((description) => ({ value: description.value, label: description })),
              ]}
            />
            {loadingSuggestionTypes && (
              <Form.Text className="text-muted">
                {translations.loadingTypes}
              </Form.Text>
            )}
          </Form.Group>

          {/* File attachment section */}
          <Form.Group className="mb-3">
            <Form.Label>{translations.attachFile}</Form.Label>
            <Form.Control
              type="file"
              id="file-input"
              onChange={handleFileChange}
              accept="*/*"
            />
            <Form.Text className="text-muted">
              {translations.allFileTypesAccepted}
            </Form.Text>

            {/* Display file error */}
            {fileError && (
              <Alert variant="danger" className="mt-2 mb-0">
                {fileError}
              </Alert>
            )}

            {/* Display selected file info */}
            {selectedFile && (
              <div className="mt-2 p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{translations.selectedFile}</strong><br />
                    <span className="text-muted">{selectedFile.name}</span><br />
                    <small className="text-muted">
                      {translations.fileSize}: {formatFileSize(selectedFile.size)} |
                      {translations.fileType}: {selectedFile.type || translations.unknown}
                    </small>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={removeFile}
                    title={translations.removeFile}
                  >
                    ✕
                  </Button>
                </div>
              </div>
            )}
          </Form.Group>
          <div className="text-center">
            <small className="text-muted">{translations.sugestionInfo}</small>
          </div>
          <div className="d-flex justify-content-center">
            <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
              {translations.send} {loading && <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>}
            </Button>
          </div>
        </Form>
      </Card.Body>

    </Card>

  );
}

export default React.memo(SuggestionForm, (prevProps, nextProps) => {
  return (
    prevProps.suggestion === nextProps.suggestion &&
    prevProps.selectedSuggestionType === nextProps.selectedSuggestionType &&
    prevProps.loadingSuggestionTypes === nextProps.loadingSuggestionTypes &&
    prevProps.suggestionTypes === nextProps.suggestionTypes &&
    prevProps.loading === nextProps.loading &&
    prevProps.translations === nextProps.translations
  );
});
